import { useCallback, useMemo } from 'react';
import { useEditorStore } from '@/stores/editorStore';
import { PropertyInput } from './PropertyInput';
import { ColorPicker } from './ColorPicker';

// Element types from svg-composer
interface Transform {
  x: number;
  y: number;
  rotation: number;
  scaleX: number;
  scaleY: number;
}

interface BaseElement {
  id: string;
  type: 'image' | 'text' | 'shape' | 'group';
  transform: Transform;
  opacity: number;
  zIndex: number;
  locked: boolean;
  visible: boolean;
}

interface ShapeElement extends BaseElement {
  type: 'shape';
  shapeType: 'rect' | 'circle' | 'ellipse' | 'path';
  fill: string;
  stroke: string;
  strokeWidth: number;
  width?: number;
  height?: number;
}

interface TextElement extends BaseElement {
  type: 'text';
  content: string;
  fontSize: number;
  fontFamily: string;
  fill: string;
  textAnchor: 'start' | 'middle' | 'end';
}

interface ImageElement extends BaseElement {
  type: 'image';
  src: string;
  width: number;
  height: number;
}

type ElementType = BaseElement | ShapeElement | TextElement | ImageElement;

interface EditorPropertiesPanelProps {
  // Returns unknown from the canvas ref, we cast it internally
  getElement?: (id: string) => unknown;
  updateElement?: (id: string, updates: Record<string, unknown>) => void;
}

export function EditorPropertiesPanel({
  getElement,
  updateElement,
}: EditorPropertiesPanelProps) {
  const { selectedElementIds } = useEditorStore();

  // Get the selected element (cast from unknown to ElementType)
  const selectedElement = useMemo((): ElementType | null => {
    if (selectedElementIds.length !== 1 || !getElement) return null;
    const element = getElement(selectedElementIds[0]);
    return (element as ElementType) || null;
  }, [selectedElementIds, getElement]);

  // Helper to convert value to number
  const toNumber = (value: number | string): number | null => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return isNaN(num) ? null : num;
  };

  // Update handlers
  const handlePositionChange = useCallback((axis: 'x' | 'y', value: number | string) => {
    if (!selectedElement || !updateElement) return;
    const numValue = toNumber(value);
    if (numValue === null) return;
    updateElement(selectedElement.id, {
      transform: {
        ...selectedElement.transform,
        [axis]: numValue,
      },
    });
  }, [selectedElement, updateElement]);

  const handleSizeChange = useCallback((dimension: 'width' | 'height', value: number | string) => {
    if (!selectedElement || !updateElement) return;
    const numValue = toNumber(value);
    if (numValue === null) return;
    // Size is stored on the element itself for shapes/images, not in transform
    updateElement(selectedElement.id, { [dimension]: numValue });
  }, [selectedElement, updateElement]);

  const handleRotationChange = useCallback((value: number | string) => {
    if (!selectedElement || !updateElement) return;
    const numValue = toNumber(value);
    if (numValue === null) return;
    updateElement(selectedElement.id, {
      transform: {
        ...selectedElement.transform,
        rotation: numValue,
      },
    });
  }, [selectedElement, updateElement]);

  const handleOpacityChange = useCallback((value: number) => {
    if (!selectedElement || !updateElement) return;
    updateElement(selectedElement.id, { opacity: value / 100 });
  }, [selectedElement, updateElement]);

  const handleFillChange = useCallback((value: string) => {
    if (!selectedElement || !updateElement) return;
    updateElement(selectedElement.id, { fill: value });
  }, [selectedElement, updateElement]);

  const handleStrokeChange = useCallback((value: string) => {
    if (!selectedElement || !updateElement) return;
    updateElement(selectedElement.id, { stroke: value });
  }, [selectedElement, updateElement]);

  const handleStrokeWidthChange = useCallback((value: number | string) => {
    if (!selectedElement || !updateElement) return;
    const numValue = toNumber(value);
    if (numValue === null) return;
    updateElement(selectedElement.id, { strokeWidth: numValue });
  }, [selectedElement, updateElement]);

  // No selection
  if (selectedElementIds.length === 0) {
    return (
      <aside className="editor-properties">
        <div className="editor-properties-header">
          <h2 className="editor-properties-title">Properties</h2>
        </div>
        <div className="editor-properties-empty">
          <p>Select an element to view and edit its properties</p>
        </div>
      </aside>
    );
  }

  // Multi-selection
  if (selectedElementIds.length > 1) {
    return (
      <aside className="editor-properties">
        <div className="editor-properties-header">
          <h2 className="editor-properties-title">Properties</h2>
        </div>
        <div className="editor-properties-content">
          <p className="property-section-title">
            {selectedElementIds.length} elements selected
          </p>

          {/* Alignment controls for multi-selection */}
          <div className="property-section">
            <h3 className="property-section-title">Align</h3>
            <div className="property-row">
              <button className="btn btn-secondary btn-sm" title="Align left">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="4" y1="4" x2="4" y2="20" />
                  <rect x="8" y="6" width="12" height="4" />
                  <rect x="8" y="14" width="8" height="4" />
                </svg>
              </button>
              <button className="btn btn-secondary btn-sm" title="Align center">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="4" x2="12" y2="20" />
                  <rect x="4" y="6" width="16" height="4" />
                  <rect x="6" y="14" width="12" height="4" />
                </svg>
              </button>
              <button className="btn btn-secondary btn-sm" title="Align right">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="20" y1="4" x2="20" y2="20" />
                  <rect x="4" y="6" width="12" height="4" />
                  <rect x="8" y="14" width="8" height="4" />
                </svg>
              </button>
            </div>
            <div className="property-row">
              <button className="btn btn-secondary btn-sm" title="Align top">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="4" y1="4" x2="20" y2="4" />
                  <rect x="6" y="8" width="4" height="12" />
                  <rect x="14" y="8" width="4" height="8" />
                </svg>
              </button>
              <button className="btn btn-secondary btn-sm" title="Align middle">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="4" y1="12" x2="20" y2="12" />
                  <rect x="6" y="4" width="4" height="16" />
                  <rect x="14" y="6" width="4" height="12" />
                </svg>
              </button>
              <button className="btn btn-secondary btn-sm" title="Align bottom">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="4" y1="20" x2="20" y2="20" />
                  <rect x="6" y="4" width="4" height="12" />
                  <rect x="14" y="8" width="4" height="8" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </aside>
    );
  }

  // Single selection - show full properties
  // If we don't have element data yet, show placeholder
  if (!selectedElement) {
    return (
      <aside className="editor-properties">
        <div className="editor-properties-header">
          <h2 className="editor-properties-title">Properties</h2>
        </div>
        <div className="editor-properties-empty">
          <p>Loading element properties...</p>
        </div>
      </aside>
    );
  }

  // Get element-specific properties
  const isShape = selectedElement.type === 'shape';
  const isText = selectedElement.type === 'text';
  const shapeElement = isShape ? (selectedElement as ShapeElement) : null;
  const textElement = isText ? (selectedElement as TextElement) : null;
  const imageElement = selectedElement.type === 'image' ? (selectedElement as ImageElement) : null;

  // Get width/height from the appropriate place
  const width = shapeElement?.width ?? imageElement?.width ?? 100;
  const height = shapeElement?.height ?? imageElement?.height ?? 100;

  return (
    <aside className="editor-properties">
      <div className="editor-properties-header">
        <h2 className="editor-properties-title">Properties</h2>
      </div>
      <div className="editor-properties-content">
        {/* Position */}
        <div className="property-section">
          <h3 className="property-section-title">Position</h3>
          <div className="property-row">
            <PropertyInput
              label="X"
              value={Math.round(selectedElement.transform.x)}
              onChange={(v) => handlePositionChange('x', v)}
            />
            <PropertyInput
              label="Y"
              value={Math.round(selectedElement.transform.y)}
              onChange={(v) => handlePositionChange('y', v)}
            />
          </div>
        </div>

        {/* Size - only for shapes and images */}
        {(isShape || selectedElement.type === 'image') && (
          <div className="property-section">
            <h3 className="property-section-title">Size</h3>
            <div className="property-row">
              <PropertyInput
                label="W"
                value={Math.round(width)}
                onChange={(v) => handleSizeChange('width', v)}
                min={1}
              />
              <PropertyInput
                label="H"
                value={Math.round(height)}
                onChange={(v) => handleSizeChange('height', v)}
                min={1}
              />
            </div>
          </div>
        )}

        {/* Rotation */}
        <div className="property-section">
          <h3 className="property-section-title">Rotation</h3>
          <div className="property-row">
            <PropertyInput
              label="°"
              value={Math.round(selectedElement.transform.rotation)}
              onChange={handleRotationChange}
              min={-360}
              max={360}
            />
          </div>
        </div>

        {/* Fill - for shapes and text */}
        {(isShape || isText) && (
          <div className="property-section">
            <h3 className="property-section-title">Fill</h3>
            <ColorPicker
              value={shapeElement?.fill ?? textElement?.fill ?? '#3498db'}
              onChange={handleFillChange}
            />
          </div>
        )}

        {/* Stroke - for shapes only */}
        {isShape && shapeElement && (
          <div className="property-section">
            <h3 className="property-section-title">Stroke</h3>
            <ColorPicker
              value={shapeElement.stroke}
              onChange={handleStrokeChange}
            />
            <div className="property-row" style={{ marginTop: '0.5rem' }}>
              <PropertyInput
                label="Width"
                value={shapeElement.strokeWidth}
                onChange={handleStrokeWidthChange}
                min={0}
                max={50}
              />
            </div>
          </div>
        )}

        {/* Text-specific properties */}
        {isText && textElement && (
          <div className="property-section">
            <h3 className="property-section-title">Font</h3>
            <div className="property-row">
              <PropertyInput
                label="Size"
                value={textElement.fontSize}
                onChange={(v) => updateElement?.(selectedElement.id, { fontSize: v })}
                min={8}
                max={200}
              />
            </div>
          </div>
        )}

        {/* Opacity */}
        <div className="property-section">
          <h3 className="property-section-title">Opacity</h3>
          <div className="property-slider">
            <input
              type="range"
              className="property-slider-input"
              min={0}
              max={100}
              value={Math.round(selectedElement.opacity * 100)}
              onChange={(e) => handleOpacityChange(Number(e.target.value))}
            />
            <span className="property-slider-value">
              {Math.round(selectedElement.opacity * 100)}%
            </span>
          </div>
        </div>
      </div>
    </aside>
  );
}
